import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, ReplaySubject } from 'rxjs';
import { filter, map, tap, switchMap } from 'rxjs/operators';

import { isNilOrEmpty } from 'helpers';

import { AssessmentInfo, GetAssessmentInfoQueryGQL, GetEntityTypesGQL } from 'generated/graphql';

import { AssessmentInfoViewModel, mapAssessmentInfoToVM } from './AssessmentInfoViewModel';

@Injectable({
  providedIn: 'root'
})
export class AssessmentLevelsService {

  private cacheSubjectViewModels = new BehaviorSubject<AssessmentInfoViewModel[]>([]);
  private cacheSubjectAssessmentInfo = new ReplaySubject<AssessmentInfo>(1);

  private ZERO_ASSESSMENT: AssessmentInfoViewModel = {
    entityTypeId: null,
    level: 0,
    description: 'No Assessment',
    detailedDescription: 'No Assessment',
    color: null,
    class: 'anull'
  };

  queryRef = this._getAssessmentInfoGQL.watch();

  /**
   *
   */
  constructor(
    private _getAssessmentInfoGQL: GetAssessmentInfoQueryGQL,
    private _getEntityTypesGQL: GetEntityTypesGQL
  ) {
    this._getEntityTypesGQL
      .fetch({
        first: 1000,
      })
      .pipe(
        map(res => res.data.getEntityTypes.nodes),
        switchMap(entityTypes => {
          return this.queryRef.valueChanges.
          pipe(
            map(d => d.data.getAssessmentInfo),
            tap(ai => this.cacheSubjectAssessmentInfo.next(ai)),
            map(ai => [...mapAssessmentInfoToVM(ai, entityTypes, this.ZERO_ASSESSMENT)])
          )
        })
      )
      .subscribe(response => {
        this.cacheSubjectViewModels.next(response);
      });
  }

  firstSetupRequired(): Observable<boolean> {
    return this.queryRef.valueChanges.pipe(
      map(({data}) => data.getAssessmentInfo.isDefault)
    );
  }

  getAllAssessmentInfoViewModels(): Observable<AssessmentInfoViewModel[]> {
    return this.cacheSubjectViewModels.asObservable().pipe(
      filter(array => !isNilOrEmpty(array)),
    );
  }

  getAssessmentInfoViewModelsForEntityType(entityTypeId: string | null = null): Observable<AssessmentInfoViewModel[]> {
    return this.cacheSubjectViewModels.asObservable().pipe(
      filter(array => !isNilOrEmpty(array)),
      map(array => array.filter(avm => avm.entityTypeId === entityTypeId)),
    );
  }

  getAssesmentInfos() {
    return this.cacheSubjectAssessmentInfo.asObservable().pipe(
      filter(a => !isNilOrEmpty(a))
    );
  }

  getAssessmentInfoVM(level: number, entityTypeId: string | null = null): Observable<AssessmentInfoViewModel> {
    return this.getAssessmentInfoViewModelsForEntityType(entityTypeId).pipe(
      map(t => t.find(avm => avm.level === (level || 0)),
     )
    );
  }

}
